import { Link } from 'react-router-dom';
import { CheckCircle2, PartyPopper } from 'lucide-react';
import type { BuyResult } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateTime, formatRub } from '@/lib/utils';

interface Props {
  result: BuyResult | null;
  onClose: () => void;
}

export function PurchaseSuccessDialog({ result, onClose }: Props) {
  return (
    <Dialog open={result != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center rounded-full bg-eol-green/20">
              <PartyPopper className="h-5 w-5 text-eol-green" />
            </div>
            <DialogTitle>Покупка совершена!</DialogTitle>
          </div>
        </DialogHeader>

        {result && (
          <div className="space-y-4">
            <div className="rounded-md bg-eol-black/40 border border-eol-green/30 p-4">
              <p className="text-sm text-eol-desc uppercase tracking-wider font-semibold">
                Товар
              </p>
              <p className="text-eol-white font-bold text-lg">{result.product.name}</p>
              {result.durationLabel && (
                <Badge variant="outline" className="mt-2">{result.durationLabel}</Badge>
              )}
              {result.expiresAt && (
                <p className="mt-2 text-xs text-eol-desc">
                  Истекает: <span className="text-eol-white">{formatDateTime(result.expiresAt)}</span>
                </p>
              )}
            </div>

            <div className="rounded-md bg-eol-black/40 border border-white/5 p-4 space-y-1 text-sm">
              {result.discountMinor > 0 && (
                <>
                  <div className="flex items-center justify-between text-eol-desc">
                    <span>Цена</span>
                    <span>{formatRub(result.originalPriceMinor)}</span>
                  </div>
                  <div className="flex items-center justify-between text-eol-green">
                    <span>Скидка по промо</span>
                    <span>−{formatRub(result.discountMinor)}</span>
                  </div>
                </>
              )}
              <div className="flex items-center justify-between text-base pt-1 border-t border-white/10">
                <span className="text-eol-white font-bold">Списано</span>
                <span className="text-eol-pink font-extrabold">{formatRub(result.pricePaidMinor)}</span>
              </div>
              {result.balanceAfter != null && (
                <div className="flex items-center justify-between text-xs text-eol-desc">
                  <span>Баланс</span>
                  <span className="text-eol-white font-semibold">{formatRub(result.balanceAfter)}</span>
                </div>
              )}
            </div>

            <div className="rounded-md bg-eol-pink/5 border border-eol-pink/20 p-3 text-sm text-eol-white flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-eol-pink mt-0.5 shrink-0" />
              <div>
                {result.deliveries > 0
                  ? 'Команды отправлены на сервер. Заходите играть - выдача займёт несколько секунд.'
                  : 'Покупка зафиксирована. Если ожидали выдачу на сервер - проверьте позже или напишите в поддержку.'}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button asChild variant="secondary">
            <Link to="/profile" onClick={onClose}>Мои покупки</Link>
          </Button>
          <Button onClick={onClose}>В магазин</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
